import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useNavigate } from "react-router-dom"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { MoreHorizontal, FileText, Archive, Trash2, Copy, ExternalLink, CheckCircle2, AlertCircle, Loader2, FileQuestion, Mail, MessageSquare, Upload, Users } from "lucide-react"
import { motion } from "motion/react"
import { Avatar } from "@/components/ui/Avatar"
import { PreviewCard, PreviewCardTrigger, PreviewCardPopup } from "@/components/ui/preview-card"
import { Menu, MenuTrigger, MenuPopup, MenuItem } from "@/components/ui/menu"
import { cn } from "@/utils/Functions"
import { useDemoUsers } from "@/hooks/common/useDemoUsers"
import type { DemoUserId } from "@/types/demo.types"
import { notifyFeature } from "@/utils/demoNotifications"
import type { Deal, DealAnalysisState } from "@/types/deal.types"
import { DEAL_TYPE_CONFIG } from "@/constants/deal.constants"
import { formatCurrency } from "@/views/deals/utils"

// Pill rendered on each card so the user can tell at a glance which deals
// have been through the analysis engine vs which are sitting unprocessed.
// Was missing entirely — every card looked the same.
const ANALYSIS_PILL: Record<DealAnalysisState, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  none:     { label: "No file",      cls: "bg-muted text-muted-foreground",                    Icon: FileQuestion },
  pending:  { label: "Pending",      cls: "bg-amber-500/10 text-amber-300 border border-amber-500/20", Icon: Loader2 },
  running:  { label: "Analyzing…",   cls: "bg-blue-500/10 text-blue-300 border border-blue-500/20",   Icon: Loader2 },
  complete: { label: "Analyzed",     cls: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20", Icon: CheckCircle2 },
  failed:   { label: "Failed",       cls: "bg-red-500/10 text-red-300 border border-red-500/20",     Icon: AlertCircle },
}

// Channel-of-origin badge — Phase 3 multi-channel deal pipeline. Surfaces
// next to the Deal type pill so the user can spot at a glance whether a
// kanban card came from email triage vs. a manual upload. Suppressed on
// the legacy 'upload' source (the demo route + manual NewDealPage flow)
// because that's the implicit baseline; only the new email/iMessage rows
// get an explicit channel marker.
const SOURCE_BADGE: Record<'email' | 'imessage' | 'manual', { Icon: typeof Mail; label: string; cls: string }> = {
  email:    { Icon: Mail,         label: "Email",    cls: "bg-blue-500/10 text-blue-300 border border-blue-500/20" },
  imessage: { Icon: MessageSquare, label: "iMessage", cls: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" },
  manual:   { Icon: Upload,       label: "Manual",   cls: "bg-muted text-muted-foreground" },
}

interface DealKanbanCardProps {
  deal: Deal
  isDragging?: boolean
  /**
   * Called when the user clicks the menu's Delete item. The page-level
   * AlertDialog owns the confirmation flow + the actual deleteDeal call
   * — the card just signals intent so deletion state lives in one place.
   * Optional so the demo route (which renders cards in spots without a
   * delete handler wired) doesn't crash.
   */
  onRequestDelete?: (deal: Deal) => void
}

export function DealKanbanCard({ deal, isDragging, onRequestDelete }: DealKanbanCardProps) {
  const navigate = useNavigate()
  const isDemo = useIsDemo()
  const { profilePictures: demoProfilePictures, userDisplayInfo: demoUserDisplayInfo } = useDemoUsers()

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: deal.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest("[data-menu]")) return
    // Phase 3 — every card lands on the intermediate /deal/:id page so the
    // user sees the source threads + participants + cross-channel docs
    // BEFORE drilling into a single contract's analysis. Pre-Phase-3 the
    // analyzed cards auto-redirected to /analysis/:contractId, which made
    // multi-thread deals invisible (only the primary contract showed up).
    navigate(`${isDemo ? "/demo" : ""}/deal/${deal.id}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleClick(e)
    }
  }

  // Same isDemo-passthrough fix as #220 — was hardcoded to true so prod
  // users saw "Demo Mode — Feature Unsupported" toasts on stub menu
  // items. Real "Open Deal" + "Delete Deal" do real work; the rest
  // still toast as Coming Soon until they're wired.
  //
  // stopPropagation is mandatory: the Menu's popup is portal-rendered
  // outside the card's DOM subtree, so handleClick's
  // `e.target.closest("[data-menu]")` bail-out doesn't catch it and
  // every menu click bubbles up to the card's onClick → navigate(...).
  // Pre-fix, "Delete" navigated to /deal/<id> before React could mount
  // the AlertDialog. The bug was invisible on the other items because
  // their handlers either navigated to the same place anyway or just
  // toasted (cloud-tester run 2026-04-30).
  const handleAction = (e: React.MouseEvent, action: string) => {
    e.stopPropagation()
    if (action === "Open Deal") {
      navigate(`${isDemo ? "/demo" : ""}/deal/${deal.id}`)
      return
    }
    if (action === "Delete Deal") {
      // Defer to the page-level AlertDialog so confirmation + the
      // deleteDeal mutation share state. Falls back to the demo-mode
      // toast when no handler is wired (e.g. inside the table view's
      // card mount where delete isn't a feature yet).
      if (onRequestDelete) {
        onRequestDelete(deal)
        return
      }
    }
    notifyFeature(action, isDemo)
  }

  const dealTypeConfig = DEAL_TYPE_CONFIG[deal.dealType]
  const actualIsDragging = isDragging || isSortableDragging

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View deal: ${deal.name}`}
      data-testid={`deal-card-${deal.id}`}
      data-deal-id={deal.id}
      className={cn(
        "group relative bg-neutral-100 dark:bg-neutral-800 border border-border rounded-xl p-3 cursor-pointer",
        "hover:border-border/80 hover:shadow-sm",
        "transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        actualIsDragging && "cursor-grabbing opacity-50 shadow-lg scale-[1.02] rotate-1",
      )}
    >
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", dealTypeConfig.color)}>Deal</span>
            {deal.sourceType && deal.sourceType !== "upload" && (() => {
              const badge = SOURCE_BADGE[deal.sourceType]
              return (
                <span
                  className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1", badge.cls)}
                  aria-label={`Source: ${badge.label}`}
                >
                  <badge.Icon className="size-3" aria-hidden="true" />
                  {badge.label}
                </span>
              )
            })()}
            {deal.analysisState && (() => {
              const pill = ANALYSIS_PILL[deal.analysisState]
              const animateIcon = deal.analysisState === "running" || deal.analysisState === "pending"
              return (
                <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1", pill.cls)}>
                  <pill.Icon className={cn("size-3", animateIcon && "animate-spin")} aria-hidden="true" />
                  {pill.label}
                </span>
              )
            })()}
          </div>

          <Menu>
            <MenuTrigger
              data-menu
              onClick={(e) => e.stopPropagation()}
              aria-label="Deal options"
              className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground opacity-0 group-hover:opacity-100"
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </MenuTrigger>
            <MenuPopup align="end" sideOffset={4} className="min-w-[140px]">
              <MenuItem onClick={(e) => handleAction(e, "Open Deal")}>
                <ExternalLink className="size-4" aria-hidden="true" />
                Open
              </MenuItem>
              <MenuItem onClick={(e) => handleAction(e, "Duplicate Deal")}>
                <Copy className="size-4" aria-hidden="true" />
                Duplicate
              </MenuItem>
              <MenuItem onClick={(e) => handleAction(e, "Archive Deal")}>
                <Archive className="size-4" aria-hidden="true" />
                Archive
              </MenuItem>
              <MenuItem onClick={(e) => handleAction(e, "Delete Deal")} className="text-destructive-foreground">
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </MenuItem>
            </MenuPopup>
          </Menu>
        </div>

        <h4 className="text-sm font-medium text-foreground line-clamp-2 mb-1">{deal.name}</h4>
        <p className="text-xs text-muted-foreground mb-3">{deal.type}</p>
        <div className="text-base font-semibold text-foreground mb-3">{formatCurrency(deal.value)}</div>

        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div className="flex items-center gap-3 text-muted-foreground/60 text-xs">
            <span className="flex items-center gap-1" aria-label={`${deal.documentsCount} documents`}>
              <FileText className="size-3" aria-hidden="true" />
              {deal.documentsCount}
            </span>
            {deal.participantCount !== undefined && deal.participantCount > 0 && (
              <span
                className="flex items-center gap-1"
                aria-label={`${deal.participantCount} participants`}
              >
                <Users className="size-3" aria-hidden="true" />
                {deal.participantCount}
              </span>
            )}
          </div>

          {deal.collaborators.length > 0 && (
            <div className="flex -space-x-1.5">
              {deal.collaborators.slice(0, 3).map((odId) => {
                const userId = odId as DemoUserId
                const userInfo = demoUserDisplayInfo[userId]

                return (
                  <PreviewCard key={userId}>
                    <PreviewCardTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Avatar src={demoProfilePictures[userId]} className="size-5 border border-card" />
                    </PreviewCardTrigger>
                    <PreviewCardPopup sideOffset={8}>
                      <div className="flex gap-3">
                        <Avatar src={demoProfilePictures[userId]} className="size-10 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{userInfo?.displayName ?? "Unknown"}</p>
                          <p className="text-xs text-muted-foreground truncate">{userInfo?.email ?? ""}</p>
                        </div>
                      </div>
                    </PreviewCardPopup>
                  </PreviewCard>
                )
              })}
              {deal.collaborators.length > 3 && (
                <div
                  className="size-5 rounded-full bg-muted border border-card flex items-center justify-center text-[10px] text-muted-foreground"
                  aria-label={`${deal.collaborators.length - 3} additional collaborators`}
                >
                  +{deal.collaborators.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
